import React, { useState, useRef, useEffect } from "react";

import {
  ChildEnvironmentIcon,
  Highlightable,
  LinkIcon,
  LoadingIcon,
  MasterIcon,
  PausedIcon,
  SuccessIcon,
  Typography,
  WarningIcon,
  WingIcon
} from "@packages/ds";
import momentjs from "moment";
import momentTZ from "moment-timezone";
import { useIntl } from "react-intl";

import { ACTIVITY_CONTEXT, MOMENTJS_TIME_FORMAT } from "Constants/constants";
import EnvironmentRoutes from "Containers/EnvironmentRoutes";
import { Link } from "ds/Button";
import * as Dialog from "ds/Dialog";
import * as Dropdown from "ds/Dropdown";
import { Tooltip } from "ds/Tooltip";
import useDecodedParams from "Hooks/useDecodedParams";
import useUrls from "Hooks/useUrls";
import { isEnvInactiveOrDeleting } from "Libs/utils";
import { getSelectors } from "Reducers/activity";
import { useEnvironmentActivities } from "Reducers/activity/activity.hooks";
import { useDeploymentType } from "Reducers/project/project/hooks/useDeploymentType";
import { useAppSelector } from "Store/hooks";

import { EnvironmentTreeData } from "../utils";

import * as S from "./EnvironmentListBar.styles";
const REJECTED_ACTIVITIES = [
  "backup",
  "restore",
  "migrate",
  "backup.delete",
  "cron"
];

type EnvironmentListBarProps = {
  className?: string;
  displayMode?: string;
  node: EnvironmentTreeData;
  titleFilter?: string;
  isBigScreen?: boolean;
};

const EnvironmentListBar = ({
  className = "",
  displayMode = "tree",
  node,
  titleFilter,
  isBigScreen = true
}: EnvironmentListBarProps) => {
  const intl = useIntl();
  const { organizationId, projectId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
  }>();

  const { shouldShowDeploymentType, hasStagedActivities } = useDeploymentType({
    environmentId: node.environmentId
  });
  const isEnvInactive = isEnvInactiveOrDeleting(node.status);
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const titleRef = useRef<HTMLDivElement>(null);

  const { inProgress } = getSelectors(ACTIVITY_CONTEXT.Project, {
    projectId,
    organizationId
  });
  const activities = useAppSelector(inProgress);

  const [environmentOverviewUrl] = useUrls("organization.project.environment", {
    environmentId: node.environmentId
  });

  const isDeploying = useEnvironmentActivities({
    allActivities: activities,
    activitesToCheck: REJECTED_ACTIVITIES,
    activityContext: ACTIVITY_CONTEXT.Environment,
    environmentId: node.environmentId
  });

  const checkEllipsis = (e: HTMLDivElement | null) => {
    if (!e) return false;
    return e.offsetWidth < e.scrollWidth;
  };

  useEffect(() => {
    if (checkEllipsis(titleRef.current)) {
      setIsEllipsisActive(true);
    } else {
      setIsEllipsisActive(false);
    }
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleWindowResize);

    return () => window.removeEventListener("resize", handleWindowResize);
  }, [windowWidth]);

  const deploymentState = node.deployment_state || {
    last_deployment_at: undefined,
    last_deployment_successful: undefined
  };
  const {
    last_deployment_at: lastDeploymentAt,
    last_deployment_successful: lastDeploymentSuccessful
  } = deploymentState;

  const lastDeploymentDuration =
    lastDeploymentAt && momentjs(lastDeploymentAt).fromNow();

  return (
    <S.Wrapper
      className={className}
      id={`environment-listbar-link-${node.environmentId}`}
      tabIndex={0}
      to={environmentOverviewUrl}
    >
      {node.isRoot ? (
        <>
          <Tooltip
            tooltip={intl.formatMessage({
              id: `environmentTree.line.root${isEnvInactive ? ".inactive" : ""}`
            })}
            analyticId={`environmentTree.line.root${
              isEnvInactive ? ".inactive" : ""
            }`}
          >
            <S.IconRootWrapper data-testid="node_is_root">
              <MasterIcon color="var(--theme-neutral-0)" />
            </S.IconRootWrapper>
          </Tooltip>
        </>
      ) : (
        <>
          {node.children?.length > 0 ? (
            <>
              <Tooltip
                tooltip={intl.formatMessage({
                  id: `environmentTree.line.parent${
                    isEnvInactive ? ".inactive" : ""
                  }`
                })}
                analyticId={`environmentTree.line.parent${
                  isEnvInactive ? ".inactive" : ""
                }`}
              >
                <S.IconParentWrapper data-testid="node_has_children">
                  <ChildEnvironmentIcon color="var(--has-children-environment-icon-fill,var(--icon-snow-fill,var(--snow)))" />
                </S.IconParentWrapper>
              </Tooltip>
            </>
          ) : (
            <>
              {isEnvInactive ? (
                <>
                  <Tooltip
                    tooltip={intl.formatMessage({
                      id: "environmentTree.line.child.inactive"
                    })}
                    analyticId="environmentTree.line.child.inactive"
                  >
                    <S.IconWrapper data-testid="node_status_inactive">
                      <ChildEnvironmentIcon color="var(--mode-vector-neutral-default)" />
                    </S.IconWrapper>
                  </Tooltip>
                </>
              ) : (
                <S.IconWrapper data-testid="node_default_icon">
                  <ChildEnvironmentIcon color="var(--mode-vector-primary-default)" />
                </S.IconWrapper>
              )}
            </>
          )}
        </>
      )}

      {isEllipsisActive ? (
        <Tooltip tooltip={node.title} analyticId={node.title}>
          <S.Title
            isRootOrNonDevelopment={node.isRoot || node.type !== "development"}
            status={node.status}
            ref={titleRef}
          >
            <Highlightable
              searchText={titleFilter}
              textToHighlight={node.title || ""}
            />{" "}
            {shouldShowDeploymentType && hasStagedActivities && (
              <Tooltip
                tooltip={intl.formatMessage({
                  id: "environment.overview.deployment_type.info"
                })}
                align="center"
                analyticId="environment.overview.deployment_type.info"
              >
                <S.TooltipContent>
                  <S.Staged
                    data-testid="staged_activity"
                    stroke="var(--color-gold-600)"
                  />
                </S.TooltipContent>
              </Tooltip>
            )}
          </S.Title>
        </Tooltip>
      ) : (
        <S.Title
          isRootOrNonDevelopment={node.isRoot || node.type !== "development"}
          status={node.status}
          ref={titleRef}
        >
          <Highlightable
            searchText={titleFilter}
            textToHighlight={node.title || ""}
          />
          {shouldShowDeploymentType && hasStagedActivities && (
            <Tooltip
              tooltip={intl.formatMessage({
                id: "environment.overview.explicit_deployment.info"
              })}
              alignOffset={5}
              align="center"
              analyticId="environment.overview.explicit_deployment.info"
            >
              <S.TooltipContent>
                <S.Staged
                  data-testid="staged_activity"
                  stroke="var(--color-gold-600)"
                />
              </S.TooltipContent>
            </Tooltip>
          )}
        </S.Title>
      )}

      {displayMode === "list" && isBigScreen && (
        <>
          <S.Type>{node.type}</S.Type>

          <S.CreatedAt data-testid="created_at_list">
            <Tooltip
              analyticId={`${momentjs(node.created_at).format(
                MOMENTJS_TIME_FORMAT
              )} (UTC${momentTZ(node.created_at).format("Z")})
                    `}
              tooltip={`${momentjs(node.created_at).format(
                MOMENTJS_TIME_FORMAT
              )} (UTC${momentTZ(node.created_at).format("Z")})
                      `}
            >
              {momentjs(node.created_at).format("D MMM YYYY")}
            </Tooltip>
          </S.CreatedAt>
        </>
      )}

      <S.Deployment>
        {isDeploying ? (
          <Dialog.Root data-testid="dialog_deploy">
            <Dialog.Trigger analyticId="environmentTree.line.deployment.activity.inprogress">
              <S.IconControl
                aria-label="enviroment-tree-progress"
                justifyContent={isBigScreen ? "space-between" : "end"}
              >
                <span>{isBigScreen && lastDeploymentDuration}</span>
                <LoadingIcon color="transparent" />
              </S.IconControl>
            </Dialog.Trigger>
            <S.Content>
              <Dialog.Description>
                <Typography.BodyText size="small" weight="semiBold">
                  {intl.formatMessage({
                    id: `environmentTree.line.deployment.activity.inprogress`,
                    defaultMessage: "Activity in progress"
                  })}
                </Typography.BodyText>
              </Dialog.Description>
            </S.Content>
          </Dialog.Root>
        ) : node.status === "paused" ? (
          <Dialog.Root data-testid="dialog_paused">
            <Dialog.Trigger analyticId="environmentTree.line.deployment.is.paused">
              <S.IconControl
                fill="transparent"
                justifyContent={isBigScreen ? "space-between" : "end"}
                aria-label="icon-control-deployment-paused"
              >
                {isBigScreen && lastDeploymentDuration}
                <PausedIcon color="var(--icon-slate-stroke,var(--slate))" />
              </S.IconControl>
            </Dialog.Trigger>
            <S.Content>
              <Dialog.Description>
                <Typography.BodyText size="small" weight="semiBold">
                  {intl.formatMessage({
                    id: `environmentTree.line.deployment.is.paused`,
                    defaultMessage: "Environment is paused"
                  })}
                </Typography.BodyText>
              </Dialog.Description>
            </S.Content>
          </Dialog.Root>
        ) : (
          <>
            {node.deployment_state && (
              <Dialog.Root data-testid="dialog_last_deployment">
                <Dialog.Trigger
                  analyticId={`environmentTree.line.deployment.last.${
                    lastDeploymentSuccessful ? "success" : "failed"
                  }`}
                >
                  <S.IconControl
                    aria-label="enviroment-tree-last"
                    justifyContent={isBigScreen ? "space-between" : "end"}
                  >
                    {isBigScreen && lastDeploymentDuration}
                    {lastDeploymentSuccessful ? (
                      <SuccessIcon
                        data-testid="dialog_last_deployment_success_icon"
                        color="var(--icon-java-fill,var(--java))"
                      />
                    ) : (
                      <WarningIcon
                        data-testid="dialog_last_deployment_warning_icon"
                        color="var(--icon-fire-fill,var(--fire))"
                      />
                    )}
                  </S.IconControl>
                </Dialog.Trigger>
                <S.Content>
                  <Dialog.Description>
                    <Typography.BodyText size="small" weight="semiBold">
                      {intl.formatMessage({
                        id: `environmentTree.line.deployment.last.${
                          lastDeploymentSuccessful ? "success" : "failed"
                        }`
                      })}

                      <S.CommitSha>
                        {intl.formatMessage(
                          { id: "environmentTree.line.commit" },
                          {
                            sha: node.head_commit?.substring(0, 7)
                          }
                        )}
                      </S.CommitSha>
                    </Typography.BodyText>
                    {lastDeploymentAt && (
                      <S.DeployedAt>
                        {`${momentjs(lastDeploymentAt).format(
                          MOMENTJS_TIME_FORMAT
                        )} (UTC${momentTZ(node.created_at).format("Z")})`}
                      </S.DeployedAt>
                    )}
                  </Dialog.Description>
                </S.Content>
              </Dialog.Root>
            )}
          </>
        )}

        {!isEnvInactive && node.deployment_target !== "local" && (
          <S.WingsDialogRoot data-testid="dialog_wings">
            <Dialog.Trigger analyticId="dedicatedenv.dialog.text">
              <S.WingsIconWrapper aria-label="dedicatedenv-icon">
                <WingIcon color="var(--mode-vector-neutral-default)" />
              </S.WingsIconWrapper>
            </Dialog.Trigger>
            <S.Content>
              <Dialog.Title>
                {intl.formatMessage({
                  id: "dedicatedenv.dialog.title"
                })}
              </Dialog.Title>
              <Dialog.Description>
                {intl.formatMessage({
                  id: "dedicatedenv.dialog.text"
                })}
              </Dialog.Description>
              <Link
                // stopPropagation() is required otherwise the event will bubble up to the parent element and prevent a new tab from opening
                onClick={e => e?.stopPropagation()}
                href={intl.formatMessage({
                  id: "links.documentation.dedicated_cluster"
                })}
              >
                {intl.formatMessage({
                  id: "learnmore"
                })}
              </Link>
            </S.Content>
          </S.WingsDialogRoot>
        )}
      </S.Deployment>

      {!isEnvInactive && node.status !== "paused" ? (
        <Dropdown.Root data-testid="environment_list_bar_action">
          <S.DropdownTrigger // preventDefault() is required otherwise the page will be redirected
            onClick={e => e?.preventDefault()}
            variant="icon"
            aria-label="link-environment-paused"
          >
            <LinkIcon />
          </S.DropdownTrigger>
          <Dropdown.Portal>
            <Dropdown.Content
              align="end"
              // stopPropagation() is required otherwise the event will bubble up to the parent element and prevent a new tab from opening
              onClick={e => e?.stopPropagation()}
            >
              <EnvironmentRoutes
                projectId={node.projectId}
                organizationId={organizationId}
                environmentId={node.environmentId}
                routeMaxNumber={5}
              />
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      ) : (
        <S.Spacer />
      )}
    </S.Wrapper>
  );
};

export default EnvironmentListBar;
